import { type Ref, useCallback, useImperativeHandle, useState } from "react";

import { showUnsavedChangesPrompt } from "commonComponents/utilities/DirtyTracking/UnsavedChangesPrompt";

import type { ISpecificationViewerRef } from "./SpecificationViewer";

interface IUseSpecificationInlineEditOptions {
    isDirty: boolean;
    requestSave: () => Promise<void>;
    onBack?: () => void;
    viewerRef: Ref<ISpecificationViewerRef>;
    /** Create mode: initial isEditMode is true, cancel/back route through `onCancelCreate`. */
    isCreating?: boolean;
    /** Required when isCreating is true. Invoked when the user discards the in-progress new spec. */
    onCancelCreate?: () => void;
}

export function useSpecificationInlineEdit({
    isDirty,
    requestSave,
    onBack,
    viewerRef,
    isCreating = false,
    onCancelCreate,
}: IUseSpecificationInlineEditOptions) {
    const [isEditMode, setIsEditMode] = useState(isCreating);

    const enterEditMode = useCallback(() => {
        setIsEditMode(true);
    }, []);

    const exitEditMode = useCallback(() => {
        setIsEditMode(false);
    }, []);

    const handleSubmit = useCallback(() => {
        void requestSave();
    }, [requestSave]);

    // Prompts to save / discard before running `action`. In create mode, exitEditMode is skipped
    // — the parent unmounts the viewer on success/cancel, so toggling here flashes the read-only
    // empty state during teardown.
    const guardDirtyEdit = useCallback(
        (action?: () => void) => {
            if (isEditMode && isDirty) {
                showUnsavedChangesPrompt(["specificationContent"], {
                    onCloseWithSave: async () => {
                        await requestSave();
                        action?.();
                    },
                    onCloseWithoutSave: () => {
                        if (!isCreating) exitEditMode();
                        action?.();
                    },
                    closable: true,
                });
            } else {
                if (isEditMode && !isCreating) exitEditMode();
                action?.();
            }
        },
        [isEditMode, isDirty, isCreating, requestSave, exitEditMode],
    );

    useImperativeHandle(
        viewerRef,
        () => ({
            guardNavigation: guardDirtyEdit,
        }),
        [guardDirtyEdit],
    );

    const handleCancel = useCallback(() => {
        if (isCreating) {
            guardDirtyEdit(onCancelCreate);
        } else {
            guardDirtyEdit();
        }
    }, [isCreating, guardDirtyEdit, onCancelCreate]);

    const handleBack = useCallback(() => {
        if (isCreating) {
            guardDirtyEdit(onCancelCreate);
            return;
        }
        if (!onBack) return;
        guardDirtyEdit(onBack);
    }, [isCreating, onCancelCreate, onBack, guardDirtyEdit]);

    return {
        isEditMode,
        enterEditMode,
        exitEditMode,
        handleSubmit,
        handleCancel,
        handleBack,
    };
}
